<template>
  <li v-if="shouldRenderMessage" :id="`message${data.id}`" :class="alignBubble">
    <div :class="wrapClass">
      <div v-if="isFailed" class="message-failed--alert">
        <woot-button
          v-tooltip.top-end="$t('CONVERSATION.TRY_AGAIN')"
          size="tiny"
          color-scheme="alert"
          variant="clear"
          icon="arrow-clockwise"
          @click="retrySendMessage"
        />
      </div>
      <div
        v-if="!isHidden"
        v-tooltip.top-start="messageToolTip"
        :class="bubbleClass"
        @contextmenu="openContextMenu($event)"
      >
        <div>
          <message-reply
            v-if="replyData"
            :key="replyData.id"
            class="message--read ph-no-capture message-reply"
            :data="replyData"
          />
        </div>
        <div>
          <message-link-preview v-if="hasLinkPreview" :preview="linkPreview" />
        </div>
        <bubble-mail-head
          :email-attributes="contentAttributes.email"
          :cc="emailHeadAttributes.cc"
          :bcc="emailHeadAttributes.bcc"
          :is-incoming="isIncoming"
        />
        <blockquote v-if="storyReply" class="story-reply-quote">
          <span>{{ $t('CONVERSATION.REPLIED_TO_STORY') }}</span>
          <bubble-image
            v-if="!hasImgStoryError && storyUrl"
            :url="storyUrl"
            @error="onStoryLoadError"
          />
          <bubble-video
            v-else-if="hasImgStoryError && storyUrl"
            :url="storyUrl"
          />
        </blockquote>
        <div v-if="isForwarded" class="forwarded-label">
          <span class="forwarded-arrow">⤷</span>
          {{ $t('CONVERSATION.FORWARDED') }}
        </div>

        <div v-if="data.content && !hasAttachments && !isMessageCall">
          <div v-if="parsedContentAttributesContent" class="reply-preview">
            {{ parsedContentAttributesContent }}
          </div>
          <bubble-text
            :message="message"
            :is-email="isEmailContentType"
            :display-quoted-button="displayQuotedButton"
          />
        </div>
        <div v-if="isMessageCall">
          <div class="message-call">
            <span class="call-icon-badge" :class="parsedCallIcon">
              <fluent-icon
                class="call-icon"
                :icon="parsedCallIcon"
                size="14"
                :view-box="parsedCallViewBox"
              />
            </span>
            <strong>{{ parsedCallStatus }}</strong>
          </div>
        </div>
        <bubble-integration
          :message-id="data.id"
          :content-attributes="contentAttributes"
          :inbox-id="data.inbox_id"
        />
        <span
          v-if="isPending && hasAttachments"
          class="chat-bubble has-attachment agent"
        >
          {{ $t('CONVERSATION.UPLOADING_ATTACHMENTS') }}
        </span>
        <div v-if="showAttachments">
          <div v-for="attachment in data.attachments" :key="attachment.id">
            <audio v-if="isIOS() && attachment.file_type === 'audio'" controls>
              <source :src="attachment.direct_file_url" type="audio/mp3" />
              Seu navegador não suporta o elemento de áudio.
            </audio>
            <bubble-image-audio-video
              v-if="
                isAttachmentImageVideoAudio(attachment.file_type) &&
                !(isIOS() && attachment.file_type === 'audio')
              "
              :attachment="attachment"
              @error="onImageLoadError"
            />
            <bubble-location
              v-else-if="attachment.file_type === 'location'"
              :latitude="attachment.coordinates_lat"
              :longitude="attachment.coordinates_long"
              :name="attachment.fallback_title"
            />
            <bubble-contact
              v-else-if="attachment.file_type === 'contact'"
              :name="data.content"
              :phone-number="attachment.fallback_title"
            />
            <instagram-image-error-placeholder
              v-else-if="hasImageError && hasInstagramStory"
            />
            <bubble-file
              v-else-if="!(isIOS() && attachment.file_type === 'audio')"
              :url="attachment.data_url"
            />
          </div>
          <template v-if="isAudioTranscription">
            <hr />
            <div class="lia-transcription">
              <img
                class="lia-transcription__avatar"
                src="~dashboard/assets/images/lia-avatar.png"
                alt=""
              />
              <span class="lia-transcription__title">
                {{ $t('INTEGRATION_SETTINGS.OPEN_AI.LIA.AUDIO_TRANSCRIPTION') }}
              </span>
            </div>
          </template>
          <bubble-text
            v-if="data.content"
            :message="message"
            :is-email="false"
            :display-quoted-button="true"
          />
        </div>
        <div
          v-if="parsedItemsFromContentAttributes.length"
          class="whatsapp-buttons-wrap"
        >
          <span
            v-for="(item, index) in parsedItemsFromContentAttributes"
            :key="index"
            class="whatsapp-button"
          >
            {{ item.title }}
          </span>
        </div>
        <bubble-actions
          :id="data.id"
          :sender="data.sender"
          :story-sender="storySender"
          :external-error="externalError"
          :story-id="`${storyId}`"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="isAWhatsAppChannel"
          :has-instagram-story="hasInstagramStory"
          :is-email="isEmailContentType"
          :is-private="data.private"
          :message-type="data.message_type"
          :message-status="status"
          :source-id="data.source_id"
          :inbox-id="data.inbox_id"
          :created-at="createdAt"
          :is-verified="isFileVerified"
        />
        <div
          v-if="templateNameFromAdditionalAttributes.length"
          class="whatsapp-buttons-wrap"
        >
          <span class="whatsapp-campaign-label">
            {{ $t('CONVERSATION.CAMPAIGN') }}:
            {{ templateNameFromAdditionalAttributes }}
          </span>
        </div>
      </div>
      <div v-else :class="bubbleClass" @contextmenu="openContextMenu($event)">
        <bubble-text
          message="<strong>Essa mensagem está oculta</strong><br/><em>Solicite que um administrador da conta desoculte a mensagem para que você possa ver o contexto</em>"
        />
      </div>
      <spinner v-if="isPending" size="tiny" />
      <div
        v-if="showAvatar"
        v-tooltip.left="tooltipForSender"
        class="sender--info"
      >
        <woot-thumbnail
          :src="sender.thumbnail"
          :username="senderNameForAvatar"
          size="16px"
        />
        <a
          v-if="isATweet && isIncoming"
          class="sender--available-name"
          :href="twitterProfileLink"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {{ sender.name }}
        </a>
      </div>
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <context-menu
        v-if="showCtxMenu"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="data"
        @open="openContextMenu"
        @close="closeContextMenu"
      />
    </div>
  </li>
</template>

<script>
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import BubbleActions from './bubble/Actions';
import BubbleFile from './bubble/File';
import BubbleImage from './bubble/Image';
import BubbleVideo from './bubble/Video';
import BubbleImageAudioVideo from './bubble/ImageAudioVideo';
import BubbleIntegration from './bubble/Integration.vue';
import BubbleLocation from './bubble/Location';
import BubbleMailHead from './bubble/MailHead';
import BubbleText from './bubble/Text';
import BubbleContact from './bubble/Contact';
import MessageReply from './MessageReply';
import MessageLinkPreview from './MessageLinkPreview';
import Spinner from 'shared/components/Spinner';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu';
import instagramImageErrorPlaceholder from './instagramImageErrorPlaceholder.vue';
import alertMixin from 'shared/mixins/alertMixin';
import contentTypeMixin from 'shared/mixins/contentTypeMixin';
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import { generateBotMessageContent } from './helpers/botMessageContentHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { mapGetters } from 'vuex';
import adminMixin from 'dashboard/mixins/isAdmin';

export default {
  components: {
    BubbleActions,
    BubbleFile,
    BubbleImage,
    BubbleVideo,
    BubbleImageAudioVideo,
    BubbleIntegration,
    BubbleLocation,
    BubbleMailHead,
    BubbleText,
    BubbleContact,
    ContextMenu,
    Spinner,
    instagramImageErrorPlaceholder,
    MessageReply,
    MessageLinkPreview,
  },
  mixins: [alertMixin, messageFormatterMixin, contentTypeMixin, adminMixin],
  props: {
    data: {
      type: Object,
      required: true,
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    isAWhatsAppChannel: {
      type: Boolean,
      default: false,
    },
    hasInstagramStory: {
      type: Boolean,
      default: false,
    },
    isWebWidgetInbox: {
      type: Boolean,
      default: false,
    },
    replyData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showContextMenu: false,
      hasImageError: false,
      contextMenuPosition: {},
      showBackgroundHighlight: false,
      hasImgStoryError: false,
    };
  },
  computed: {
    ...mapGetters({
      currentRole: 'getCurrentRole',
      isEmbedding: 'isEmbedding',
      currentUser: 'getCurrentUser',
      getAccount: 'accounts/getAccount',
    }),
    shouldRenderMessage() {
      return (
        this.hasAttachments ||
        this.data.content ||
        this.isEmailContentType ||
        this.isAnIntegrationMessage
      );
    },
    emailMessageContent() {
      const {
        html_content: { full: fullHTMLContent } = {},
        text_content: { full: fullTextContent } = {},
      } = this.contentAttributes.email || {};
      return fullHTMLContent || fullTextContent || '';
    },
    displayQuotedButton() {
      if (this.emailMessageContent.includes('<blockquote')) {
        return true;
      }

      if (!this.isIncoming) {
        return false;
      }

      return false;
    },
    message() {
      // If the message is an email, emailMessageContent would be present
      // In that case, we would use letter package to render the email
      if (this.emailMessageContent && this.isIncoming) {
        return this.emailMessageContent;
      }

      const botMessageContent = generateBotMessageContent(
        this.contentType,
        this.contentAttributes,
        {
          noResponseText: this.$t('CONVERSATION.NO_RESPONSE'),
          csat: {
            ratingTitle: this.$t('CONVERSATION.RATING_TITLE'),
            feedbackTitle: this.$t('CONVERSATION.FEEDBACK_TITLE'),
          },
        }
      );

      let baseMessage = this.formatMessage(
        this.data.content,
        this.isATweet,
        this.data.private
      );

      if (this.contentType === 'input_csat') {
        return this.$t('CONVERSATION.CSAT_REPLY_MESSAGE') + botMessageContent;
      }

      if (this.externalError && this.shouldDisplayApiErrors) {
        const errorText = this.externalError;
        const regex = /^(\d+):\s(.+?)\s-\s(.+)$/;
        const match = errorText.match(regex);

        const moreInfoLink = `<a href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/" target="_blank">clique aqui</a>`;

        if (match) {
          const [, code, title, userMessage] = match;
          return `
          ${baseMessage}
          <div style="margin-top: 16px; text-align: center;"><strong>- Erro -</strong></div>
          <div style="margin-top: 8px;"><strong>Código:</strong> ${code} (${title})</div>
          <div style="margin-top: 8px;"><strong>Mensagem:</strong> ${userMessage}</div>
          <div style="margin-top: 8px;"><strong>Mais informações:</strong> ${moreInfoLink}</div>
        `;
        }

        return `
        ${baseMessage}
        <div style="margin-top: 16px; text-align: center;"><strong>- Erro -</strong></div>
        <div style="margin-top: 8px;">${errorText}</div>
        <div style="margin-top: 8px;"><strong>Mais informações:</strong> ${moreInfoLink}</div>
      `;
      }

      return baseMessage + botMessageContent;
    },
    parsedContentAttributesContent() {
      try {
        const raw = this.data.content_attributes;
        if (!raw) return '';

        const parsed =
          typeof raw === 'string' ? JSON.parse(raw.replace(/=>/g, ':')) : raw;

        const replyTo = parsed?.reply_to;

        const replyToParsed =
          typeof replyTo === 'string'
            ? JSON.parse(replyTo.replace(/=>/g, ':'))
            : replyTo;

        return replyToParsed?.content || '';
      } catch (e) {
        return '';
      }
    },
    parsedItemsFromContentAttributes() {
      try {
        const raw = this.data.content_attributes;
        if (!raw) return [];

        const parsed =
          typeof raw === 'string' ? JSON.parse(raw.replace(/=>/g, ':')) : raw;

        if (parsed.items && Array.isArray(parsed.items)) {
          return parsed.items;
        }

        for (const value of Object.values(parsed)) {
          if (
            value &&
            typeof value === 'object' &&
            value.items &&
            Array.isArray(value.items)
          ) {
            return value.items;
          }
        }

        return [];
      } catch (e) {
        return [];
      }
    },
    templateNameFromAdditionalAttributes() {
      try {
        const { campaign_id, template_params } =
          this.data.raw_additional_attributes || {};
        return campaign_id &&
          typeof template_params === 'object' &&
          template_params?.name
          ? template_params.name
          : '';
      } catch {
        return '';
      }
    },
    contextMenuEnabledOptions() {
      return {
        copy: this.hasText,
        delete: this.showDelete,
        cannedResponse: this.isOutgoing && this.hasText,
      };
    },
    contentAttributes() {
      return this.data.content_attributes || {};
    },
    externalError() {
      return this.contentAttributes.external_error || null;
    },
    sender() {
      return this.data.sender || {};
    },
    status() {
      return this.data.status;
    },
    storySender() {
      return this.contentAttributes.story_sender || null;
    },
    storyId() {
      return this.contentAttributes.story_id || null;
    },
    storyUrl() {
      return this.contentAttributes.story_url || null;
    },
    storyReply() {
      return this.storyUrl && this.hasInstagramStory;
    },
    contentType() {
      const {
        data: { content_type: contentType },
      } = this;
      return contentType;
    },
    twitterProfileLink() {
      const additionalAttributes = this.sender.additional_attributes || {};
      const { screen_name: screenName } = additionalAttributes;
      return `https://twitter.com/${screenName}`;
    },
    alignBubble() {
      const { message_type: messageType } = this.data;
      const isCentered = messageType === MESSAGE_TYPE.ACTIVITY;
      const isLeftAligned = [
        MESSAGE_TYPE.INCOMING,
        MESSAGE_TYPE.CALL_INCOMING,
      ].includes(messageType);
      const isRightAligned = [
        MESSAGE_TYPE.OUTGOING,
        MESSAGE_TYPE.TEMPLATE,
        MESSAGE_TYPE.CALL_OUTGOING,
        MESSAGE_TYPE.CLOSING_REASON,
      ].includes(messageType);
      return {
        center: isCentered,
        left: isLeftAligned,
        right: isRightAligned,
        'has-context-menu': this.showContextMenu,
        'has-tweet-menu': this.isATweet,
        'has-bg': this.showBackgroundHighlight,
      };
    },
    createdAt() {
      return this.contentAttributes.external_created_at || this.data.created_at;
    },
    isBubble() {
      return [0, 1, 3, 4, 5, 6].includes(this.data.message_type);
    },
    isClosingReason() {
      return this.data.message_type === MESSAGE_TYPE.CLOSING_REASON;
    },
    isIncoming() {
      return this.data.message_type === MESSAGE_TYPE.INCOMING;
    },
    isOutgoing() {
      return this.data.message_type === MESSAGE_TYPE.OUTGOING;
    },
    isTemplate() {
      return this.data.message_type === MESSAGE_TYPE.TEMPLATE;
    },
    isAnIntegrationMessage() {
      return this.contentType === 'integrations';
    },
    emailHeadAttributes() {
      return {
        email: this.contentAttributes.email,
        cc: this.contentAttributes.cc_emails,
        bcc: this.contentAttributes.bcc_emails,
      };
    },
    hasAttachments() {
      return !!(this.data.attachments && this.data.attachments.length > 0);
    },
    isAudioTranscription() {
      return (
        !!this.data.content &&
        this.hasAttachments &&
        this.data.attachments.some(
          attachment => attachment.file_type === 'audio'
        )
      );
    },
    isMessageDeleted() {
      return this.contentAttributes.deleted;
    },
    hasText() {
      return !!this.data.content;
    },
    tooltipForSender() {
      const name = this.senderNameForAvatar;
      const { message_type: messageType } = this.data;
      const showTooltip =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.TEMPLATE;
      return showTooltip
        ? {
            content: `${this.$t('CONVERSATION.SENT_BY')} ${name}`,
          }
        : false;
    },
    messageToolTip() {
      if (this.isMessageDeleted) {
        return false;
      }
      if (this.isFailed) {
        return this.externalError ? '' : this.$t(`CONVERSATION.SEND_FAILED`);
      }
      return false;
    },
    wrapClass() {
      return {
        wrap: this.isBubble,
        'activity-wrap': !this.isBubble,
        'is-pending': this.isPending,
        'is-failed': this.isFailed,
        'is-email': this.isEmailContentType,
      };
    },
    bubbleClass() {
      return {
        bubble: this.isBubble,
        'is-private': this.data.private,
        'is-closing-reason': this.isClosingReason,
        'is-image': this.hasMediaAttachment('image'),
        'is-video': this.hasMediaAttachment('video'),
        'is-text': this.hasText,
        'is-from-bot': this.isSentByBot,
        'is-failed': this.isFailed,
        'is-email': this.isEmailContentType,
      };
    },
    isPending() {
      return this.data.status === MESSAGE_STATUS.PROGRESS;
    },
    isFailed() {
      return this.externalError && this.shouldDisplayApiErrors;
    },
    isHidden() {
      return this.data.hidden;
    },
    isSentByBot() {
      if (this.isPending || this.isFailed) return false;
      return !this.sender.type || this.sender.type === 'agent_bot';
    },
    shouldShowContextMenu() {
      return !(
        this.isFailed ||
        this.isPending ||
        (this.isHidden && !this.isAdmin)
      );
    },
    errorMessage() {
      const { meta } = this.data;
      return meta ? meta.error : '';
    },
    showAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        return true;
      }
      return this.isATweet && this.isIncoming && this.sender;
    },
    senderNameForAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        const { name = this.$t('CONVERSATION.BOT') } = this.sender || {};
        return name;
      }
      return '';
    },
    showDelete() {
      const roles = ['administrator', 'supervisor', 'team_supervisor'];
      if (!roles.includes(this.currentRole)) return false;

      return this.hasText || this.hasAttachments;
    },
    hasLinkPreview() {
      return !!this.data.content_attributes?.link_preview;
    },
    linkPreview() {
      return this.hasLinkPreview
        ? JSON.parse(this.data.content_attributes.link_preview)
        : {};
    },
    isFileVerified() {
      return this.data.additional_attributes?.verified ?? false;
    },
    showCtxMenu() {
      if (this.currentRole == 'embed' || this.isEmbedding) {
        return false;
      }
      return this.isBubble && !this.isMessageDeleted;
    },
    shouldDisplayApiErrors() {
      const account = this.getAccount(this.currentUser?.account_id);
      const showErrors = account.custom_attributes?.show_api_error_codes;

      return Boolean(showErrors);
    },
    isMessageCall() {
      if (
        [MESSAGE_TYPE.CALL_INCOMING, MESSAGE_TYPE.CALL_OUTGOING].includes(
          this.data.message_type
        )
      ) {
        return true;
      }
      if (
        [
          MESSAGE_STATUS.CALL_INITIATED,
          MESSAGE_STATUS.CALL_RINGING,
          MESSAGE_STATUS.CALL_IN_PROGRESS,
          MESSAGE_STATUS.CALL_FAILED,
          MESSAGE_STATUS.CALL_COMPLETED,
          MESSAGE_STATUS.CALL_PERMISSION_REPLY,
        ].includes(this.data.status)
      ) {
        return true;
      }

      return false;
    },
    parsedCallIcon() {
      const icons = {};
      icons[MESSAGE_STATUS.CALL_INITIATED] = 'call-progress';
      icons[MESSAGE_STATUS.CALL_RINGING] = 'call-progress';
      icons[MESSAGE_STATUS.CALL_IN_PROGRESS] = 'call-progress';
      icons[MESSAGE_STATUS.CALL_FAILED] =
        this.data.message_type == MESSAGE_TYPE.CALL_OUTGOING
          ? 'call-outgoing'
          : 'call-missed';
      icons[MESSAGE_STATUS.CALL_COMPLETED] =
        this.data.message_type == MESSAGE_TYPE.CALL_OUTGOING
          ? 'call-outgoing'
          : 'call-incoming';
      icons[MESSAGE_STATUS.CALL_PERMISSION_REPLY] = 'call-permission-reply';

      return icons[this.data.status] || 'call-progress';
    },
    parsedCallViewBox() {
      if (this.data.message_type === MESSAGE_TYPE.CALL_OUTGOING) {
        return '0 0 30 30';
      }
      return '0 0 24 24';
    },
    parsedCallStatus() {
      const labels = {};
      labels[MESSAGE_STATUS.CALL_INITIATED] = this.$t(
        'CONVERSATION.CALL_INITIATED'
      );
      labels[MESSAGE_STATUS.CALL_RINGING] = this.$t(
        'CONVERSATION.CALL_RINGING'
      );
      labels[MESSAGE_STATUS.CALL_IN_PROGRESS] = this.$t(
        'CONVERSATION.CALL_IN_PROGRESS'
      );
      labels[MESSAGE_STATUS.CALL_FAILED] = this.$t('CONVERSATION.CALL_FAILED');
      labels[MESSAGE_STATUS.CALL_COMPLETED] = this.$t(
        'CONVERSATION.CALL_COMPLETED'
      );
      labels[MESSAGE_STATUS.CALL_PERMISSION_REPLY] = this.$t(
        'CONVERSATION.CALL_PERMISSION_REPLY'
      );

      return labels[this.data.status] || '';
    },
    showAttachments() {
      if (
        !this.isPending &&
        this.hasAttachments &&
        this.isMessageCall &&
        this.data.status === MESSAGE_STATUS.CALL_COMPLETED
      ) {
        return true;
      }

      if (!this.isPending && this.hasAttachments && !this.isMessageCall) {
        return true;
      }

      return false;
    },
    isForwarded() {
      return this.data.raw_additional_attributes?.forwarded === true;
    },
  },
  watch: {
    data() {
      this.hasImageError = false;
      this.hasImgStoryError = false;
    },
  },
  mounted() {
    this.hasImageError = false;
    this.hasImgStoryError = false;
    bus.$on(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    window.bus.$on('close-all-message-context-menus', this.closeContextMenu);
    this.setupHighlightTimer();
  },
  beforeDestroy() {
    bus.$off(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    window.bus.$off('close-all-message-context-menus', this.closeContextMenu);
    clearTimeout(this.higlightTimeout);
  },
  methods: {
    isAttachmentImageVideoAudio(fileType) {
      return ['image', 'audio', 'video'].includes(fileType);
    },
    isIOS() {
      const userAgent = navigator.userAgent;
      return (
        /iPhone|iPad|iPod/i.test(userAgent) &&
        (/Safari/i.test(userAgent) ||
          (/AppleWebKit/i.test(userAgent) &&
            !/Chrome|Firefox/i.test(userAgent)))
      );
    },
    hasMediaAttachment(type) {
      if (this.hasAttachments && this.data.attachments.length > 0) {
        const { attachments = [{}] } = this.data;
        const { file_type: fileType } = attachments[0];
        return fileType === type && !this.hasImageError;
      }
      if (this.storyReply) {
        return true;
      }
      return false;
    },
    handleContextMenuClick() {
      this.showContextMenu = !this.showContextMenu;
    },
    async retrySendMessage() {
      await this.$store.dispatch('sendMessageWithData', this.data);
    },
    onImageLoadError() {
      this.hasImageError = true;
    },
    onStoryLoadError() {
      this.hasImgStoryError = true;
    },
    openContextMenu(e) {
      const shouldSkipContextMenu =
        e.target?.classList.contains('skip-context-menu') ||
        e.target?.tagName.toLowerCase() === 'a' ||
        (this.isHidden && !this.isAdmin);
      if (shouldSkipContextMenu || getSelection().toString()) {
        return;
      }

      e.preventDefault();
      if (e.type === 'contextmenu') {
        this.$track(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
      }
      window.bus.$emit('close-all-message-context-menus');
      this.contextMenuPosition = {
        x: e.pageX || e.clientX,
        y: e.pageY || e.clientY,
      };
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.showContextMenu = false;
      this.contextMenuPosition = { x: null, y: null };
    },
    setupHighlightTimer() {
      if (Number(this.$route.query.messageId) !== Number(this.data.id)) {
        return;
      }

      this.showBackgroundHighlight = true;
      const HIGHLIGHT_TIMER = 1000;
      this.higlightTimeout = setTimeout(() => {
        this.showBackgroundHighlight = false;
      }, HIGHLIGHT_TIMER);
    },
  },
};
</script>
<style lang="scss">
.wrap {
  > .bubble {
    min-width: 128px;

    &.is-image,
    &.is-video {
      padding: 0;
      overflow: hidden;

      .image,
      .video {
        max-width: 32rem;
        padding: var(--space-micro);

        > img,
        > video {
          border-radius: var(--border-radius-medium);
        }
        > video {
          height: 100%;
          object-fit: cover;
          width: 100%;
        }
      }
      .video {
        height: 18rem;
      }
    }

    &.is-private.is-closing-reason {
      background: #ffd8a8 !important;
      border: 1px solid rgba(255, 146, 43, 0.7) !important;

      .message-text__wrap,
      .text-content,
      .message-text--metadata,
      .time {
        color: #2d3748 !important;
      }

      .link {
        color: #2d3748 !important;
      }
    }

    &.is-image.is-text > .message-text__wrap,
    &.is-video.is-text > .message-text__wrap {
      max-width: 32rem;
      padding: var(--space-small) var(--space-normal);
    }

    &.is-private .file.message-text__wrap {
      .file--icon {
        color: var(--w-400);
      }
      .text-block-title {
        color: #3c4858;
      }
      .download.button {
        color: var(--w-400);
      }
    }

    &.is-private.is-text > .message-text__wrap .link {
      color: var(--w-700);
    }
    &.is-private.is-text > .message-text__wrap .prosemirror-mention-node {
      font-weight: var(--font-weight-black);
      background: none;
      border-radius: var(--border-radius-small);
      padding: 0;
      color: var(--color-body);
      text-decoration: underline;
    }

    &.is-from-bot {
      background: var(--v-400);
      .message-text--metadata .time {
        color: var(--v-50);
      }
      &.is-private .message-text--metadata .time {
        color: var(--s-400);
      }
    }

    &.is-failed {
      background: var(--r-200);

      .message-text--metadata .time {
        color: var(--r-50);
      }
    }
  }

  &.is-pending {
    position: relative;
    opacity: 0.8;

    .spinner {
      position: absolute;
      bottom: var(--space-smaller);
      right: var(--space-smaller);
    }

    > .is-image.is-text.bubble > .message-text__wrap {
      padding: 0;
    }
  }
}

.wrap.is-email {
  --bubble-max-width: 84% !important;
}

.sender--info {
  align-items: center;
  color: var(--b-700);
  display: inline-flex;
  padding: var(--space-smaller) 0;

  .sender--available-name {
    font-size: var(--font-size-mini);
    margin-left: var(--space-smaller);
  }
}

.message-failed--alert {
  color: var(--r-900);
  flex-grow: 1;
  text-align: right;
  margin-top: var(--space-smaller) var(--space-smaller) 0 0;
}

li.left,
li.right {
  display: flex;
  align-items: flex-end;
}

li.left.has-tweet-menu .context-menu {
  margin-bottom: var(--space-medium);
}

li.has-bg {
  background: var(--w-75);
}

li.right .context-menu-wrap {
  margin-left: auto;
}

li.right {
  flex-direction: row-reverse;
  justify-content: flex-end;

  .wrap.is-pending {
    margin-left: auto;
  }

  .wrap.is-failed {
    display: flex;
    align-items: flex-end;
    margin-left: auto;
  }
}

.has-context-menu {
  background: var(--color-background);
}

.context-menu {
  position: relative;
}

/* Markdown styling */

.bubble .text-content {
  p code {
    background-color: var(--s-75);
    display: inline-block;
    line-height: 1;

    border-radius: var(--border-radius-small);
    padding: var(--space-smaller);
  }

  pre {
    background-color: var(--s-75);
    border-color: var(--s-75);
    color: var(--s-800);
    border-radius: var(--border-radius-normal);
    padding: var(--space-small);
    margin-top: var(--space-smaller);
    margin-bottom: var(--space-small);
    display: block;
    line-height: 1.7;
    white-space: pre-wrap;

    code {
      background-color: transparent;
      color: var(--s-800);
      padding: 0;
    }
  }

  blockquote {
    border-left: var(--space-micro) solid var(--s-75);
    color: var(--s-800);
    margin: var(--space-smaller) 0;
    padding: var(--space-small) var(--space-small) 0 var(--space-normal);
  }
}

.right .bubble .text-content {
  p code {
    background-color: var(--w-600);
    color: var(--white);
  }

  pre {
    background-color: var(--w-800);
    border-color: var(--w-700);
    color: var(--white);

    code {
      background-color: transparent;
      color: var(--white);
    }
  }

  blockquote {
    border-left: var(--space-micro) solid var(--w-400);
    color: var(--white);

    p {
      color: var(--w-75);
    }
  }
}

.story-reply-quote {
  border-left: var(--space-micro) solid var(--s-75);
  color: var(--s-600);
  margin: var(--space-small) var(--space-normal) 0;
  padding: var(--space-small) var(--space-small) 0 var(--space-small);

  .message-reply {
    margin-bottom: var(--space-small);
  }
}

.reply-preview {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: rgba(255, 255, 255, 0.08);
  color: var(--white);
  font-size: var(--font-size-smaller);
  padding: 2px 6px 3px;
  border-left: 2px solid rgba(255, 255, 255, 0.4);
  margin-bottom: 4px;
  opacity: 0.9;
  border-radius: var(--border-radius-small);
  line-height: 1.4;
}
.whatsapp-buttons-wrap {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.whatsapp-button {
  background-color: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 13px;
  user-select: none;
  line-height: 1.2;
}

.whatsapp-campaign-label {
  background-color: #ffffff;
  color: #000000;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  user-select: none;
  line-height: 1.2;
}

.message-call {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  .call-icon-badge {
    background-color: #22469b;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 33px;
    width: 33px;
    margin-right: 15px;
    &.call-missed {
      background-color: #cf2823;
      .call-icon {
        left: 20px;
        top: 15px;
        position: absolute;
        width: 20px;
      }
    }
    &.call-outgoing {
      background-color: #fff;
      .call-icon {
        left: 21px;
        position: absolute;
        width: 20px;
        color: #22469b;
      }
    }
    &.call-progress {
      background-color: #44ce4b;
    }
    &.call-permission-reply {
      background-color: #ffcc00;
    }
    .call-icon {
      color: #fff;
    }
  }
}

.forwarded-label {
  font-size: 12px;
  color: rgba(78, 91, 110, 0.6);
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  width: fit-content;
}

.forwarded-arrow {
  font-size: 14px;
  color: rgba(91, 111, 129, 0.6);
  margin-left: 0;
}

.lia-transcription {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  margin-bottom: var(--space-small);
}

.lia-transcription__avatar {
  flex-shrink: 0;
  width: 2.9rem;
  height: 2.9rem;
  border-radius: 50%;
}

.lia-transcription__title {
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
}
</style>
